import { creditCostFor, type CreditPricing } from '@/store/slices/aiCredits';

import { normaliseLocalizedCopy, type AdLanguageId, type LocalizedCopy } from './ad-language';
import type { PlayableRuntime } from './playable-preview';

import {
  adApiRequest,
  adSyncBody,
  listAdRows,
  normaliseAdBrand,
  normaliseAdSubject,
  normaliseBrandButton,
  normaliseLegalLine,
  normaliseVersionArtStyle,
  upsertAdRow,
  type AdApiResult,
  type AdBrand,
  type AdSubject,
  type BrandButton,
  type AdSubjectType,
  type AdVersionArtStyle,
  type LegalLine,
  type Palette,
} from './banner-kit-api';

/**
 * Mini-games (playables): branded HTML5 mini-game ads. A mini-game is one spark-creatives `creatives` row
 * (`media_type='playable'`) with the same generic `ad_params` / `ad_versions` columns as a banner kit. Its
 * template's AI art is rendered by a `playable_assets` job onto one version (which then locks); preview, export and
 * the hosted embed package that version with the HTML5 runtime. Wire types mirror spark-creatives
 * `pkg/playables/types.go` (snake_case).
 */

/* ---------- JSON values (template config is free-form, validated server-side against the template schema) ---------- */

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Narrow an unknown wire value to a JSON object ({} otherwise). The payload came from JSON, so its leaves are JSON. */
export const asJsonObject = (value: unknown): JsonObject => (isRecord(value) ? (value as JsonObject) : {});

/* ---------- wire types ---------- */

/** The template ids (the registry may grow; unknown ids stay strings). */
export type PlayableTemplateId = 'scratch-reveal' | 'offer-wheel' | 'pick-a-gift' | 'symbol-match' | string;

/** The on-screen copy of a mini-game. */
export interface PlayableCopy {
  headline: string;
  instruction: string;
  reveal_label: string;
  cta: string;
}

/** The offer revealed on the end card. */
export interface PlayableOffer {
  title: string;
  terms: string;
  tc_url: string;
}

/** Attract-phase timing: the hint pulse and the auto-reveal without interaction. */
export interface PlayableTiming {
  hint_ms: number;
  auto_reveal_ms: number;
}

export interface PlayableAudio {
  default_muted: boolean;
}

/** The export formats a mini-game can target. */
export type PlayableFormat = '300x600' | '320x480' | '480x320' | '336x280' | '300x250' | 'responsive' | string;

/** A mini-game's editable document (`ad_params`). */
export interface PlayableParams {
  subject_type: AdSubjectType;
  subject: AdSubject;
  /** '' until the owner picks a template. */
  template_id: PlayableTemplateId;
  schema_version: number;
  /** The template's own fields (see the template `schema`). */
  config: JsonObject;
  copy: PlayableCopy;
  offer: PlayableOffer;
  /** Palette and fonts populated. */
  brand: AdBrand;
  legal: LegalLine;
  formats: PlayableFormat[];
  art_direction: string;
  /** The absolute CTA destination. */
  landing_url: string;
  timing: PlayableTiming;
  audio: PlayableAudio;
  /** The ad's language: a brand website language id (`mx`, `br`, `en`...), every word the ad shows is in it. '' on a
   *  document made before ads had a language (it renders as before: the promotion's English-first copy, the game
   *  text in the language its copy reads in). Not an art input: the art is text-free. */
  language: AdLanguageId;
  /** The subject's copy in `language` (the website's own text, else Lumio's translation), filled when it arrives;
   *  the locked words read it (`localizedSubject`). Absent until then, and on older documents. */
  localized_copy?: LocalizedCopy;
}

/** A version's rendered art. `errors` names an asset op that failed (its credits were refunded). */
export interface PlayableVersionAssets {
  background_url?: string;
  /** Format (`"300x250"`) -> its own background plate (a `plate_per_format` asset, HTML5 banner templates). */
  plates?: Record<string, string>;
  /** Asset key -> the whole sprite sheet. */
  sprites?: Record<string, string>;
  /** Asset key -> the sheet sliced into its cells. */
  sliced?: Record<string, string[]>;
  errors?: Record<string, string>;
  /** A mini-game plate's automatic check (spark-creatives `playables.PlateQA`): the kept render's status and reason. */
  plate_qa?: PlayablePlateQA;
}

/** Why a mini-game's background needs a look (spark-creatives `playables.PlateQAReason*`): the first check it failed,
 *  or the check could not run. */
export type PlayablePlateQAReason = 'middle_busy' | 'text' | 'money_objects' | 'unavailable';

/** A mini-game plate's check, as the Studio reads it (the per-render reads stay on the backend for debugging). */
export interface PlayablePlateQA {
  /** `failed` only when every redraw still failed the check. */
  status: 'ok' | 'needs_review' | 'failed';
  /** Absent when ok, or on a plate checked before reasons were recorded. */
  reason?: PlayablePlateQAReason;
}

/** One mini-game version (`ad_versions[]`, newest first). */
export interface PlayableVersion {
  created_at: string;
  locked?: boolean;
  params: PlayableParams;
  assets: PlayableVersionAssets;
  /** What the art was drawn with (see {@link AdVersionArtStyle}). */
  art_style?: AdVersionArtStyle;
}

/** Hosted-embed counters (30 days), all zero until an embed is live. */
export interface PlayableStats {
  impressions: number;
  interactions: number;
  reveals: number;
  clicks: number;
}

/** A mini-game entity as listed. */
export interface Playable {
  id: string;
  tenant_id: string;
  name: string;
  media_type: 'playable';
  image_url: string;
  status: string;
  archived: boolean;
  job_id?: string;
  created_at?: string;
  ad_params: PlayableParams;
  ad_versions: PlayableVersion[];
  stats: PlayableStats;
}

/** Body for creating (no `id`) or updating a mini-game (same rules as a kit upsert). */
export interface PlayableUpsert {
  id?: string;
  name?: string;
  image_url?: string;
  status?: string;
  job_id?: string;
  ad_params?: PlayableParams;
  ad_version?: Omit<PlayableVersion, 'created_at' | 'locked'>;
  ad_version_at?: string;
}

/* ---------- templates ---------- */

/** The subset of JSON Schema (draft-07) the template schemas use, as the config form reads it. */
export interface PlayableSchema {
  type?: 'object' | 'array' | 'string' | 'number' | 'integer' | 'boolean';
  title?: string;
  description?: string;
  properties?: Record<string, PlayableSchema>;
  required?: string[];
  items?: PlayableSchema;
  enum?: JsonPrimitive[];
  /** `color` | `uri` | `textarea` for strings. */
  format?: string;
  /** A string's regex (a hex-colour pattern makes the form offer a colour picker). */
  pattern?: string;
  minimum?: number;
  maximum?: number;
  minItems?: number;
  maxItems?: number;
  default?: JsonValue;
}

/** One AI asset a template renders, priced by its own action row. */
export interface PlayableTemplateAsset {
  key: string;
  price_action: string;
  /** `plate` (the background, stored as `background_url`), `sheet` (a sprite sheet, stored whole and sliced),
   *  `plate_per_format` (one background per format, stored in `plates`) or `cutout` (the subject isolated on a
   *  transparent background, stored as `sprites.subject`). */
  kind: 'plate' | 'sheet' | 'plate_per_format' | 'cutout' | string;
}

/** What a template is: a mini-game (the Mini-games tab) or a non-gamified animated banner (the HTML5 Banners tab). */
export type PlayableTemplateCategory = 'game' | 'banner' | string;

export interface PlayableTemplate {
  id: PlayableTemplateId;
  /** English label (a data value; render it through `<Text>`). */
  label: string;
  /** `game` or `banner` (older payloads carry none: every template was a game). */
  category: PlayableTemplateCategory;
  subjects: AdSubjectType[];
  formats: PlayableFormat[];
  schema_version: number;
  schema: PlayableSchema;
  assets: PlayableTemplateAsset[];
  /** The export profile this template defaults to (`html5-banner-zip` for an HTML5 banner style; absent for games). */
  default_profile?: string;
}

/** The sprite sheets that draw the art style's character as one of their items (the offer wheel's icons, the symbol
 *  match's symbols); every other asset (backgrounds, gifts, vaults, cut-outs) is drawn without it. */
const CHARACTER_SHEETS = new Set(['icons', 'symbols']);

/** Whether a template's art features the art style's character (one item of an icon or symbol sheet), so a
 *  character pick changes what it draws. HTML5 banner styles and most games never do. Pure. */
export const templateFeaturesCharacter = (template: Pick<PlayableTemplate, 'assets'> | undefined): boolean =>
  Boolean(template?.assets.some((a) => a.kind === 'sheet' && CHARACTER_SHEETS.has(a.key)));

/** An export profile. `group` drives the Web / Ad servers / In-app grouping. */
export interface PlayableProfile {
  id: string;
  group: 'web' | 'ad-servers' | 'in-app' | string;
  label: string;
  package: 'html' | 'zip' | string;
  single_file: boolean;
  /** The banner-kit animated HTML5 export takes this profile (web and ad-server display profiles). */
  kits?: boolean;
}

/** `GET /creatives/playables/templates`. */
export interface PlayableTemplatesResponse {
  templates: PlayableTemplate[];
  profiles: PlayableProfile[];
  features: { html5: boolean; embed: boolean };
  /** The built HTML5 runtime + template bundles for the live in-browser preview; null when not built. */
  runtime: PlayableRuntime | null;
}

/* ---------- preview / export / embed ---------- */

export interface PlayablePreviewRequest {
  version_at: string;
  format: PlayableFormat;
  /** Unsaved edits to preview instead of the version's own params. */
  params?: PlayableParams;
}

export interface PlayableExportRequest {
  version_at: string;
  profile: string;
  formats: PlayableFormat[];
  /** The click-through written into the ad (e.g. the version's tracking link to a landing page); omitted = the
   *  version's own `landing_url`, else the subject's CTA link on the brand's domain. An ad server's own clickTag wins. */
  landing_url?: string;
}

export interface PlayableExportFile {
  format: string;
  url?: string;
  bytes?: number;
  within_budget?: boolean;
  violations?: string[];
  error?: string;
}

export interface PlayableExportResult {
  files: PlayableExportFile[];
  zip_url: string;
  /** The static backup image per format. */
  backups: Array<{ format: string; url: string }>;
}

export interface PlayableEmbedRequest {
  version_at: string;
  format: PlayableFormat;
  /** The click-through of the hosted embed (as {@link PlayableExportRequest.landing_url}). */
  landing_url?: string;
}

export interface PlayableEmbedResult {
  public_id: string;
  url: string;
  /** The iframe snippet to paste. */
  snippet: string;
}

/** The Lumio config fill: a suggested template (when none was chosen), its config, the copy and the palette. */
export interface PlayableConfigFill {
  ok: boolean;
  template_id?: PlayableTemplateId;
  config?: JsonObject;
  copy?: Partial<PlayableCopy>;
  palette?: Palette;
  /** The brand website's CTA button, when the site has a readable theme (the document's own button wins; the theme
   *  fills it when the document has none). */
  button?: BrandButton;
  /** The brand website's fonts, when the backend could read them (heading and body Google Font families). */
  fonts?: { heading?: string; body?: string };
}

/* ---------- normalisation ---------- */

const str = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);
const num = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;
const isSubjectType = (value: unknown): value is AdSubjectType =>
  value === 'promotion' || value === 'game' || value === 'gamification';

/** Attract defaults: hint after 2.5 s, auto-reveal after 7 s. */
export const DEFAULT_PLAYABLE_TIMING: PlayableTiming = { hint_ms: 2500, auto_reveal_ms: 7000 };

/** Full mini-game params from a wire value (a blank draft's `{}` included). */
export const normalisePlayableParams = (raw: unknown): PlayableParams => {
  const p = isRecord(raw) ? raw : {};
  const c = isRecord(p.copy) ? p.copy : {};
  const o = isRecord(p.offer) ? p.offer : {};
  const t = isRecord(p.timing) ? p.timing : {};
  const a = isRecord(p.audio) ? p.audio : {};
  const localized = normaliseLocalizedCopy(p.localized_copy);
  return {
    subject_type: isSubjectType(p.subject_type) ? p.subject_type : 'promotion',
    subject: normaliseAdSubject(p.subject),
    template_id: str(p.template_id),
    schema_version: num(p.schema_version, 1),
    config: asJsonObject(p.config),
    copy: { headline: str(c.headline), instruction: str(c.instruction), reveal_label: str(c.reveal_label), cta: str(c.cta) },
    offer: { title: str(o.title), terms: str(o.terms), tc_url: str(o.tc_url) },
    brand: normaliseAdBrand(p.brand),
    legal: normaliseLegalLine(p.legal),
    formats: Array.isArray(p.formats) ? p.formats.filter((f): f is string => typeof f === 'string') : [],
    art_direction: str(p.art_direction),
    landing_url: str(p.landing_url),
    timing: {
      hint_ms: num(t.hint_ms, DEFAULT_PLAYABLE_TIMING.hint_ms),
      auto_reveal_ms: num(t.auto_reveal_ms, DEFAULT_PLAYABLE_TIMING.auto_reveal_ms),
    },
    audio: { default_muted: typeof a.default_muted === 'boolean' ? a.default_muted : true },
    language: str(p.language),
    ...(localized ? { localized_copy: localized } : {}),
  };
};

const PLATE_QA_STATUSES: readonly string[] = ['ok', 'needs_review', 'failed'];
const PLATE_QA_REASONS: readonly string[] = ['middle_busy', 'text', 'money_objects', 'unavailable'];

/** The plate check's status and reason; null when absent or malformed. Pure. */
const normalisePlateQA = (raw: unknown): PlayablePlateQA | null => {
  if (!isRecord(raw) || typeof raw.status !== 'string' || !PLATE_QA_STATUSES.includes(raw.status)) return null;
  const reason = typeof raw.reason === 'string' && PLATE_QA_REASONS.includes(raw.reason) ? raw.reason : '';
  return {
    status: raw.status as PlayablePlateQA['status'],
    ...(reason ? { reason: reason as PlayablePlateQAReason } : {}),
  };
};

const normaliseAssets = (raw: unknown): PlayableVersionAssets => {
  const a = isRecord(raw) ? raw : {};
  const plateQA = normalisePlateQA(a.plate_qa);
  return {
    ...(typeof a.background_url === 'string' && a.background_url ? { background_url: a.background_url } : {}),
    ...(isRecord(a.plates) ? { plates: a.plates as Record<string, string> } : {}),
    ...(isRecord(a.sprites) ? { sprites: a.sprites as Record<string, string> } : {}),
    ...(isRecord(a.sliced) ? { sliced: a.sliced as Record<string, string[]> } : {}),
    ...(isRecord(a.errors) ? { errors: a.errors as Record<string, string> } : {}),
    ...(plateQA ? { plate_qa: plateQA } : {}),
  };
};

const normaliseVersion = (raw: unknown): PlayableVersion | null => {
  if (!isRecord(raw) || typeof raw.created_at !== 'string') return null;
  const artStyle = normaliseVersionArtStyle(raw.art_style);
  return {
    created_at: raw.created_at,
    locked: raw.locked === true,
    params: normalisePlayableParams(raw.params),
    assets: normaliseAssets(raw.assets),
    ...(artStyle ? { art_style: artStyle } : {}),
  };
};

const normaliseStats = (raw: unknown): PlayableStats => {
  const s = isRecord(raw) ? raw : {};
  return {
    impressions: num(s.impressions, 0),
    interactions: num(s.interactions, 0),
    reveals: num(s.reveals, 0),
    clicks: num(s.clicks, 0),
  };
};

/** A listed mini-game row with its params, versions and stats normalised. */
export const normalisePlayable = (raw: Record<string, unknown>): Playable => ({
  id: str(raw.id),
  tenant_id: str(raw.tenant_id, String(raw.tenant_id ?? '')),
  name: str(raw.name),
  media_type: 'playable',
  image_url: str(raw.image_url),
  status: str(raw.status, 'draft'),
  archived: raw.archived === true || raw.status === 'archived',
  ...(typeof raw.job_id === 'string' && raw.job_id ? { job_id: raw.job_id } : {}),
  ...(typeof raw.created_at === 'string' ? { created_at: raw.created_at } : {}),
  ad_params: normalisePlayableParams(raw.ad_params),
  ad_versions: (Array.isArray(raw.ad_versions) ? raw.ad_versions : [])
    .map(normaliseVersion)
    .filter((v): v is PlayableVersion => v !== null),
  stats: normaliseStats(raw.stats),
});

const normaliseTemplates = (raw: Record<string, unknown>): PlayableTemplatesResponse => {
  const features = isRecord(raw.features) ? raw.features : {};
  const templates = (Array.isArray(raw.templates) ? raw.templates : []).filter(isRecord).map(
    (t): PlayableTemplate => ({
      id: str(t.id),
      label: str(t.label, str(t.id)),
      category: str(t.category) || 'game',
      subjects: (Array.isArray(t.subjects) ? t.subjects : []).filter(isSubjectType),
      formats: (Array.isArray(t.formats) ? t.formats : []).filter((f): f is string => typeof f === 'string'),
      schema_version: num(t.schema_version, 1),
      schema: isRecord(t.schema) ? (t.schema as PlayableSchema) : {},
      assets: (Array.isArray(t.assets) ? t.assets : [])
        .filter(isRecord)
        .map((a) => ({
          key: str(a.key),
          price_action: str(a.price_action),
          // The background is the one plate; every other asset is a sheet (older payloads carry no kind).
          kind: str(a.kind, str(a.key) === 'background' ? 'plate' : 'sheet'),
        }))
        .filter((a) => a.key !== ''),
      ...(str(t.default_profile) ? { default_profile: str(t.default_profile) } : {}),
    }),
  );
  return {
    templates,
    profiles: (Array.isArray(raw.profiles) ? raw.profiles : []).filter(isRecord).map((p) => ({
      id: str(p.id),
      group: str(p.group),
      label: str(p.label, str(p.id)),
      package: str(p.package),
      single_file: p.single_file === true,
      // Older payloads carry no flag: the kit export takes the web and ad-server profiles.
      kits: typeof p.kits === 'boolean' ? p.kits : str(p.group) === 'web' || str(p.group) === 'ad-servers',
    })),
    features: { html5: features.html5 === true, embed: features.embed === true },
    runtime: normaliseRuntime(raw.runtime),
  };
};

/** The runtime bundles of the registry: null unless the runtime and at least one template bundle are present. */
const normaliseRuntime = (raw: unknown): PlayableRuntime | null => {
  if (!isRecord(raw) || typeof raw.js !== 'string' || raw.js === '' || !isRecord(raw.templates)) return null;
  const templates: Record<string, { js: string; css: string }> = {};
  Object.entries(raw.templates).forEach(([id, t]) => {
    if (isRecord(t) && typeof t.js === 'string' && t.js !== '') templates[id] = { js: t.js, css: str(t.css) };
  });
  if (Object.keys(templates).length === 0) return null;
  return { version: str(raw.version), contract: num(raw.contract, 1), js: raw.js, css: str(raw.css), templates };
};

/* ---------- endpoints ---------- */

const tenantPath = (tenantId: number): string => `/creatives/tenant/${encodeURIComponent(String(tenantId))}`;
const playablePath = (tenantId: number, id: string): string => `${tenantPath(tenantId)}/playables/${encodeURIComponent(id)}`;

/** List a brand's mini-games, newest first (archived ones flagged, stats attached). Throws when unreachable. */
export const listPlayables = async (tenantId: number, signal?: AbortSignal): Promise<Playable[]> => {
  const rows = await listAdRows<Record<string, unknown>>(`${tenantPath(tenantId)}/playables`, 'listPlayables', signal);
  return rows.filter(isRecord).map(normalisePlayable);
};

/** Create (no id) or update a mini-game. Returns the entity id ('' when unconfigured). Throws on a failed request. */
export const upsertPlayable = (tenantId: number, body: PlayableUpsert, signal?: AbortSignal): Promise<string> =>
  upsertAdRow(`${tenantPath(tenantId)}/playables`, body, signal);

// The template registry is static per deploy: fetched once per page load and shared (a failure clears it).
let templatesRequest: Promise<PlayableTemplatesResponse | null> | null = null;

/** The template registry, export profiles and backend features. Null when unconfigured or the read failed. */
export const fetchPlayableTemplates = (): Promise<PlayableTemplatesResponse | null> => {
  if (!templatesRequest) {
    templatesRequest = adApiRequest<Record<string, unknown>>('get', '/creatives/playables/templates').then((res) => {
      if (res.ok) return normaliseTemplates(res.data);
      templatesRequest = null;
      return null;
    });
  }
  return templatesRequest;
};

/** The Lumio config fill for a mini-game (free, synchronous). `params` carries unsaved edits (the chosen template
 *  among them); `ok:false` when the model produced nothing usable. */
export const fillPlayableConfig = async (
  tenantId: number,
  playableId: string,
  params?: PlayableParams,
  signal?: AbortSignal,
): Promise<PlayableConfigFill> => {
  const res = await adApiRequest<Record<string, unknown>>(
    'post',
    '/creatives/generate',
    adSyncBody(tenantId, 'playable', 'playable-config', { playable_id: playableId, ...(params ? { params } : {}) }),
    signal,
  );
  if (!res.ok || res.data.ok === false) return { ok: false };
  const d = res.data;
  const copy = isRecord(d.copy) ? d.copy : undefined;
  const palette = isRecord(d.palette) ? d.palette : undefined;
  const button = normaliseBrandButton(d.button);
  const fonts = isRecord(d.fonts) ? d.fonts : undefined;
  return {
    ok: true,
    ...(typeof d.template_id === 'string' && d.template_id ? { template_id: d.template_id } : {}),
    ...(isRecord(d.config) ? { config: asJsonObject(d.config) } : {}),
    ...(copy
      ? {
          copy: Object.fromEntries(
            (['headline', 'instruction', 'reveal_label', 'cta'] as const)
              .filter((k) => typeof copy[k] === 'string')
              .map((k) => [k, copy[k] as string]),
          ) as Partial<PlayableCopy>,
        }
      : {}),
    ...(palette
      ? { palette: { primary: str(palette.primary), accent: str(palette.accent), bg: str(palette.bg), text: str(palette.text) } }
      : {}),
    ...(button ? { button } : {}),
    ...(fonts && (str(fonts.heading) || str(fonts.body))
      ? {
          fonts: {
            ...(str(fonts.heading) ? { heading: str(fonts.heading) } : {}),
            ...(str(fonts.body) ? { body: str(fonts.body) } : {}),
          },
        }
      : {}),
  };
};

/** Render a single-file preview of a version in one format (free). Show it ONLY in a sandboxed iframe
 *  (`sandbox="allow-scripts"`, never `allow-same-origin`). 409 until the HTML5 runtime is built. */
export const previewPlayable = async (
  tenantId: number,
  playableId: string,
  body: PlayablePreviewRequest,
  signal?: AbortSignal,
): Promise<AdApiResult<{ html: string }>> => {
  const res = await adApiRequest<{ html?: string }>('post', `${playablePath(tenantId, playableId)}/preview`, body, signal);
  if (!res.ok) return res;
  return { ok: true, data: { html: str(res.data.html) } };
};

/** Package a version for an export profile, per format, plus a ZIP of all of them (free). 409 until the HTML5
 *  runtime is built (or export media is not configured). */
export const exportPlayable = async (
  tenantId: number,
  playableId: string,
  body: PlayableExportRequest,
  signal?: AbortSignal,
): Promise<AdApiResult<PlayableExportResult>> => {
  const res = await adApiRequest<{ files?: PlayableExportFile[] | null; zip_url?: string; backups?: PlayableExportResult['backups'] | null }>(
    'post',
    `${playablePath(tenantId, playableId)}/export`,
    body,
    signal,
  );
  if (!res.ok) return res;
  return {
    ok: true,
    data: {
      files: Array.isArray(res.data.files) ? res.data.files : [],
      zip_url: str(res.data.zip_url),
      backups: Array.isArray(res.data.backups) ? res.data.backups : [],
    },
  };
};

/** Publish a hosted embed of a version in one format (free). 409 when hosted embeds are not configured here. */
export const embedPlayable = async (
  tenantId: number,
  playableId: string,
  body: PlayableEmbedRequest,
  signal?: AbortSignal,
): Promise<AdApiResult<PlayableEmbedResult>> => {
  const res = await adApiRequest<{ public_id?: string; url?: string; snippet?: string }>(
    'post',
    `${playablePath(tenantId, playableId)}/embed`,
    body,
    signal,
  );
  if (!res.ok) return res;
  return { ok: true, data: { public_id: str(res.data.public_id), url: str(res.data.url), snippet: str(res.data.snippet) } };
};

/* ---------- pure helpers ---------- */

/** The mini-game templates of a registry (category `game`): what the Mini-games tab offers. Pure. */
export const gameTemplates = (templates: readonly PlayableTemplate[]): PlayableTemplate[] =>
  templates.filter((tpl) => tpl.category === 'game');

/** The HTML5 banner styles of a registry (category `banner`): what the HTML5 Banners tab offers. Pure. */
export const bannerTemplates = (templates: readonly PlayableTemplate[]): PlayableTemplate[] =>
  templates.filter((tpl) => tpl.category === 'banner');

/** Whether a playable belongs to the HTML5 Banners tab: its template is a known `banner` template (the exact inverse
 *  of {@link isMiniGame}, so the two libraries split one list). Pure. */
export const isHtml5Banner = (playable: Pick<Playable, 'ad_params'>, templates: readonly PlayableTemplate[]): boolean =>
  !isMiniGame(playable, templates);

/** Whether a playable belongs to the Mini-games tab: it does unless its template is a known `banner` template (a
 *  draft with no template yet, or one whose template the registry no longer lists, stays a mini-game). Pure. */
export const isMiniGame = (playable: Pick<Playable, 'ad_params'>, templates: readonly PlayableTemplate[]): boolean => {
  const id = playable.ad_params.template_id;
  if (!id) return true;
  return templates.find((tpl) => tpl.id === id)?.category !== 'banner';
};

/** One planned render of a Generate art: a template asset and, for a `plate_per_format` asset, the format it paints.
 *  `id` is the backend's op id (the job's planned key and the version's `assets.errors` key): the asset key, or
 *  `plate:<WxH>` for one format's plate. */
export type PlayableAssetOp = PlayableTemplateAsset & { id: string; format?: string };

/** The formats a per-format plate is painted for: the chosen formats the template takes, in the template's order,
 *  else (none chosen) the template's first format (mirrors the backend's `PlateFormats`). Pure. */
export const playablePlateFormats = (template: PlayableTemplate, chosen: readonly string[]): string[] => {
  const out = template.formats.filter((f) => f !== '*' && chosen.includes(f));
  if (out.length > 0) return out;
  const first = template.formats[0];
  return first && first !== '*' ? [first] : [];
};

/** The asset ops a template's Generate art runs for this config and these formats: every template asset, except the
 *  `container` sheet when the owner chose a custom container skin, with a `plate_per_format` asset expanded into one
 *  op per plate format ({@link playablePlateFormats}). Mirrors the backend's op list. Pure. */
export const playableAssetOps = (
  template: PlayableTemplate,
  config: JsonObject,
  formats: readonly string[] = [],
): PlayableAssetOp[] =>
  template.assets.flatMap((a): PlayableAssetOp[] => {
    if (a.key === 'container' && config.container_skin === 'custom') return [];
    if (a.kind === 'plate_per_format') {
      return playablePlateFormats(template, formats).map((format) => ({ ...a, id: `plate:${format}`, format }));
    }
    return [{ ...a, id: a.key }];
  });

/** The credit cost of a list of asset ops: the sum of each op's own price row. Undefined while any op is unpriced
 *  (the backend fails closed on a missing row), so the cost chip stays hidden rather than under-quoting. */
export const playableOpsCostFor = (
  ops: readonly PlayableTemplateAsset[],
  pricing: CreditPricing | null | undefined,
): number | undefined => {
  let total = 0;
  for (const op of ops) {
    const cost = creditCostFor(pricing, op.price_action);
    if (cost === undefined) return undefined;
    total += cost;
  }
  return total;
};

/** The credit cost of a Generate art: every asset op of the template for this config (and formats), minus those
 *  `have` (the target version's assets) already holds, because the backend resumes a version rather than charging
 *  twice. */
export const playableAssetsCostFor = (
  template: PlayableTemplate | undefined,
  config: JsonObject,
  pricing: CreditPricing | null | undefined,
  have?: PlayableVersionAssets,
  formats?: readonly string[],
): number | undefined =>
  template ? playableOpsCostFor(playableRemainingOps(template, config, have, formats), pricing) : undefined;

/** Whether a version's assets hold an op's output: the plate as `background_url`, one format's plate in `plates`,
 *  the cut-out as its sprite, a sheet as its whole image AND its sliced cells (the backend's `VersionAssets.Has`). */
export const playableAssetHas = (
  assets: PlayableVersionAssets | undefined,
  op: PlayableTemplateAsset & { format?: string },
): boolean => {
  if (!assets) return false;
  if (op.kind === 'plate') return Boolean(assets.background_url);
  if (op.kind === 'plate_per_format') return Boolean(op.format && assets.plates?.[op.format]);
  if (op.kind === 'cutout') return Boolean(assets.sprites?.[op.key]);
  return Boolean(assets.sprites?.[op.key]) && (assets.sliced?.[op.key]?.length ?? 0) > 0;
};

/** The asset ops a Generate art still has to render into a version holding `have`. Pure. */
export const playableRemainingOps = (
  template: PlayableTemplate,
  config: JsonObject,
  have?: PlayableVersionAssets,
  formats?: readonly string[],
): PlayableAssetOp[] => playableAssetOps(template, config, formats).filter((op) => !playableAssetHas(have, op));

/** Whether a version holds any rendered art. Pure. */
export const playableHasArt = (assets: PlayableVersionAssets | undefined): boolean =>
  Boolean(assets?.background_url) ||
  Object.values(assets?.plates ?? {}).some(Boolean) ||
  Object.values(assets?.sprites ?? {}).some(Boolean) ||
  Object.values(assets?.sliced ?? {}).some((cells) => cells.length > 0);

/** A version's art without its failure notes (a copy of what rendered, for a fork that keeps it). Pure. */
export const playableArtOf = (assets: PlayableVersionAssets | undefined): PlayableVersionAssets => {
  const copy = JSON.parse(JSON.stringify(assets ?? {})) as PlayableVersionAssets;
  delete copy.errors;
  return copy;
};

/** Whether an edit changes what the AI art is painted from: the template (its scenery and sheets), the subject,
 *  the brand name and colours, the art direction, or the container skin (a custom one drops the container sheet).
 *  The copy, offer, legal line, formats, timing, the logo (the brand's own, never drawn by the model: the sheets only
 *  borrow its colours) and the other game settings are drawn live by the runtime, so they keep the art. Mirrors the
 *  backend's plate and sheet prompts. Pure. */
export const playableArtChanged = (a: PlayableParams, b: PlayableParams): boolean => {
  const pa = a.brand.palette;
  const pb = b.brand.palette;
  return (
    a.template_id !== b.template_id ||
    a.subject_type !== b.subject_type ||
    a.subject.id !== b.subject.id ||
    a.subject.name !== b.subject.name ||
    a.subject.image_url !== b.subject.image_url ||
    a.brand.name !== b.brand.name ||
    (pa?.primary ?? '') !== (pb?.primary ?? '') ||
    (pa?.accent ?? '') !== (pb?.accent ?? '') ||
    (pa?.bg ?? '') !== (pb?.bg ?? '') ||
    a.art_direction.trim() !== b.art_direction.trim() ||
    (a.config.container_skin ?? null) !== (b.config.container_skin ?? null)
  );
};

/** A version's content without its identity or lock (an upsert's `ad_version`). */
export type PlayableVersionEntry = Omit<PlayableVersion, 'created_at' | 'locked'>;

const cloneParams = (p: PlayableParams): PlayableParams => JSON.parse(JSON.stringify(p)) as PlayableParams;

/** The entry that forks `source` for an edit to `next`: it keeps the source's art while the edit does not reach the
 *  art ({@link playableArtChanged}), else it starts with none so the next Generate paints art that matches. Pure. */
export const playableForkEntry = (source: PlayableVersion, next: PlayableParams): PlayableVersionEntry => {
  if (playableArtChanged(source.params, next)) return { params: cloneParams(next), assets: {} };
  // The kept art was drawn with the source's art style, so the fork still says so.
  return {
    params: cloneParams(next),
    assets: playableArtOf(source.assets),
    ...(source.art_style ? { art_style: { ...source.art_style } } : {}),
  };
};

/** Where a Generate art renders and what it renders (see {@link planPlayableRun}). */
export interface PlayableRunPlan {
  /** Fill the selected version in place. */
  reuse: boolean;
  /** The new version's content when not reusing. */
  entry: PlayableVersionEntry;
  /** The asset ops the run renders and charges. */
  ops: PlayableAssetOp[];
}

/** Where a Generate art renders and what it renders (and charges):
 *  - the selected version itself while it is unlocked and still misses some art (a first run, or a stopped one);
 *  - else a new version: carrying a generated version's art when the document still matches it and only part of
 *    it rendered (a failed op is retried alone), or empty (a fresh set of art).
 *  Pure. */
export const planPlayableRun = (
  selected: PlayableVersion | undefined,
  doc: PlayableParams,
  template: PlayableTemplate,
  /** The art style's character the next run draws differs from the one the selected version was drawn with (a
   *  template whose sheets feature it), or the brand's active art style differs from the one it was drawn with: its
   *  art holds the old character / look, so nothing is carried or filled in around. */
  opts?: { characterChanged?: boolean; styleChanged?: boolean },
): PlayableRunPlan => {
  const all = playableAssetOps(template, doc.config, doc.formats);
  const fresh: PlayableRunPlan = { reuse: false, entry: { params: cloneParams(doc), assets: {} }, ops: all };
  if (!selected) return fresh;
  if (!selected.locked) {
    // Art already painted with another character is not filled in around (two characters in one version).
    if ((opts?.characterChanged || opts?.styleChanged) && playableHasArt(selected.assets)) return fresh;
    const remaining = playableRemainingOps(template, doc.config, selected.assets, doc.formats);
    return remaining.length > 0 ? { reuse: true, entry: fresh.entry, ops: remaining } : fresh;
  }
  if (
    opts?.characterChanged ||
    opts?.styleChanged ||
    playableArtChanged(selected.params, doc) ||
    !playableHasArt(selected.assets)
  ) {
    return fresh;
  }
  const carried = playableArtOf(selected.assets);
  const remaining = playableRemainingOps(template, doc.config, carried, doc.formats);
  if (remaining.length === 0) return fresh;
  // The carried art was drawn with the selected version's art style, so the retry's version still says so.
  const entry: PlayableVersionEntry = {
    params: cloneParams(doc),
    assets: carried,
    ...(selected.art_style ? { art_style: { ...selected.art_style } } : {}),
  };
  return { reuse: false, entry, ops: remaining };
};

/** Whether a version is generated: locked by its run, or carrying every asset its template renders (a fork that
 *  kept a generated version's art). Such a version can be exported. `doc` is the editor's working document of an
 *  unlocked version (the draft's own document, which may run ahead of the stored one until its save lands), so a
 *  size or setting just added (one with no art yet) counts. Pure. */
export const playableVersionGenerated = (
  version: PlayableVersion | undefined,
  template: PlayableTemplate | undefined,
  doc?: PlayableParams,
): boolean => {
  if (!version) return false;
  if (version.locked) return true;
  if (!template || !playableHasArt(version.assets)) return false;
  const p = doc ?? version.params;
  return playableRemainingOps(template, p.config, version.assets, p.formats).length === 0;
};

/** Deep equality of two JSON documents regardless of key order (the database reorders object keys). Pure. */
export const jsonEqual = (a: unknown, b: unknown): boolean => stableJson(a) === stableJson(b);

const stableJson = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .filter((k) => value[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableJson(value[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value ?? null);
};
